import {isValid} from "date-fns"
import {Frequency, getIncrementFn} from "@indebted/payment-arrangement/Slider/Frequency"

import {PaymentArrangementSetup, Repayment} from "./paymentArrangement/setup"

function calculatePaymentAmount(outstandingBalance: number, numberOfPayments: number): number {
	if (numberOfPayments <= 0) throw new Error("Number of payments must be positive")
	return Math.floor(outstandingBalance / numberOfPayments)
}

function generateRepaymentSchedule(
	startDate: Date,
	frequency: Frequency,
	paymentAmount: number,
	numberOfPayments: number,
	outstandingBalance?: number,
): Repayment[] {
	if (!isValid(startDate)) throw new Error("Invalid start date")
	const schedule: Repayment[] = []
	let date = new Date(startDate)
	const increment = getIncrementFn(frequency)
	let total = 0
	for (let i = 0; i < numberOfPayments; i++) {
		let amount = paymentAmount
		// Add remainder to the first payment
		if (i === 0 && outstandingBalance !== undefined) {
			const totalBase = paymentAmount * numberOfPayments
			const diff = outstandingBalance - totalBase
			amount = paymentAmount + diff
		}
		schedule.push({
			PaymentDate: date,
			PaymentAmount: amount, // in cents, integer
		})
		total += amount
		date = increment(date)
	}
	if (total !== outstandingBalance) {
		console.error(`Total amount does not match outstanding balance: ${total} !== ${outstandingBalance}`)
	}
	return schedule
}

export function calculatePaymentArrangementSetup(params: {
	configuration: {
		Currency: string
		RecommendedFrequency: Frequency
		Recommendations: Record<Frequency, {NumberOfPayments: number}>
	}
	OutstandingBalance: number
	frequency?: Frequency
	numberOfPayments?: number
	startDate: Date
}): PaymentArrangementSetup {
	// If numberOfPayments is not passed, use the default from config
	const numberOfPayments =
		params.numberOfPayments ??
		params.configuration.Recommendations[params.frequency ?? params.configuration.RecommendedFrequency]
			.NumberOfPayments

	const paymentAmount = calculatePaymentAmount(params.OutstandingBalance, numberOfPayments)
	const repaymentSchedule = generateRepaymentSchedule(
		params.startDate,
		params.frequency ?? params.configuration.RecommendedFrequency,
		paymentAmount,
		numberOfPayments,
		params.OutstandingBalance,
	)

	return {
		Currency: params.configuration.Currency,
		PaymentAmount: paymentAmount,
		Frequency: params.frequency ?? params.configuration.RecommendedFrequency,
		NumberOfPayments: numberOfPayments,
		StartDate: params.startDate,
		EndDate: repaymentSchedule[repaymentSchedule.length - 1].PaymentDate,
		Payments: repaymentSchedule,
	}
}
